import { matchPath } from "react-router-dom"

import { Badge, Link, type ItemType } from "@/atoms"
import { NAMES_ROUTES } from "@/const/routes.constants"
import {
  isAiDiscoveryEnabled,
  isAiDiscoveryLive,
  isFeatureFlagMaybe,
  isLiveAiPoliciesEnabled,
  Permission
} from "@/domain/user"
import { APPLICATIONS_DEFAULT_QUERY_STRING } from "@/helpers/filters.helper"
import { isSelfHosted } from "@/helpers/self-hosted.helper"
import {
  AiDiscovery,
  AppstoreOutlined,
  AuditOutlined,
  BankOutlined,
  CompassOutlined,
  SortAscendingOutlined
} from "@/icons"
import { LabelPrimaryNavigation } from "@/molecules/LabelPrimaryNavigation"

import styles from "../SideNavigation.module.scss"

import type { TFunction } from "@/hooks/useTranslation"
import type { FeatureStatusType } from "@/molecules/FeatureStatusTag"
import type { CustomProps, ItemMenu } from ".."

export const getLabel = (
  props: ItemMenu & { countByChildren?: number; tooltip?: boolean }
) => {
  const { label, key, children, countByChildren } = props

  if (countByChildren)
    return (
      <LabelPrimaryNavigation subMenu label={label} count={countByChildren} />
    )

  const isChildrenExist = !!children?.length

  const { count, beta, isNew, soon, defaultQueryString, tooltip } =
    props.customProps || {}
  const to = `${key}${defaultQueryString ? `?${defaultQueryString}` : ""}`

  let typeTag: FeatureStatusType | undefined

  if (beta) {
    typeTag = "beta"
  }

  if (isNew) {
    typeTag = "new"
  }

  if (soon) {
    typeTag = "soon"
  }

  if (isChildrenExist) {
    if (typeTag) return <LabelPrimaryNavigation label={label} tag={typeTag} />

    return label
  }

  if (count)
    return <LabelPrimaryNavigation to={to} label={label} count={count} />

  if (typeTag)
    return (
      <LabelPrimaryNavigation
        to={to}
        label={label}
        tag={typeTag}
        tooltip={tooltip ? label : undefined}
      />
    )

  return (
    <Link to={to} className={styles.linkMenuSideNavigation}>
      {label}
    </Link>
  )
}

export const getItem = (
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: ItemMenu[],
  customProps?: CustomProps
) => {
  const countByChildren = children?.reduce((acc, item) => {
    if (item.customProps?.count) return acc + item.customProps.count

    return acc
  }, 0)

  return {
    key,
    "data-level-item-menu-side-navigation": !icon && !!children ? "2" : "",
    icon: countByChildren ? (
      <Badge dot className="badge-item-menu-side-nav-ks">
        {icon}
      </Badge>
    ) : (
      icon
    ),
    children,
    label: getLabel({ label, key, children, customProps, countByChildren }),
    customProps
  }
}

export const getIncidentsCount = (pathname: string) => {
  try {
    const incidents = matchPath("/triage/incidents/:incident?", pathname)
    const count =
      !!incidents?.params.incident &&
      JSON.parse(atob(incidents.params.incident)).length

    return count
  } catch {
    return undefined
  }
}

export const getMenuItems = (
  attr: {
    t: TFunction
    isDemo: boolean
    hasPermission: (permission: Permission) => boolean | undefined
  } & Record<string, any>
) => {
  const { isDemo, pathname, collapsed, t } = attr
  const { DISCOVERY, TRIAGE, GOVERNANCE, REPORTS } = NAMES_ROUTES
  const { ROOT: DISCOVERY_ROOT } = DISCOVERY
  const { ROOT: TRIAGE_ROOT, ISSUES } = TRIAGE
  const { ROOT: GOVERNANCE_ROOT, WORKFLOWS, SCAN_HISTORY } = GOVERNANCE
  const { ROOT: REPORTS_ROOT } = REPORTS
  const tKey = "navigation."
  const issuesKey = `/${TRIAGE_ROOT}/${ISSUES}`
  const soon = !isDemo
  const tooltip = !collapsed
  const enableIncidentsExist = !isFeatureFlagMaybe(attr.adrIncidentsEnabledFF)
  const issueDefaultQueryString = "issueStatus=open"
  let issueQueryString = `${issueDefaultQueryString}&runtime=${attr.kortexInstalled === false ? "UNDETERMINED" : "YES"}`
  if (attr.isLowRuntimeCoverage) issueQueryString = issueDefaultQueryString

  return [
    getItem(t(`${tKey}dashboard`), "/", <AppstoreOutlined />),
    getItem(t(`${tKey}discovery`), "discovery", <CompassOutlined />, [
      getItem(t(`${tKey}branch`), "/discovery/branch"),
      getItem(
        t(`${tKey}applications`),
        "/discovery/applications",
        undefined,
        undefined,
        { defaultQueryString: APPLICATIONS_DEFAULT_QUERY_STRING }
      ),
      getItem(t(`${tKey}images`), "/discovery/images"),
      getItem(
        t(`${tKey}baseImages`),
        "/discovery/baseImages",
        undefined,
        undefined,
        { removeOnSelfHosting: true }
      ),
      getItem(
        t(`${tKey}vm`),
        `/${DISCOVERY_ROOT}/${DISCOVERY.VM}`,
        undefined,
        undefined,
        // Self-hosted ingests VM sensors too, so it shows the page once a VM is deployed
        { vmInstancesEnabledFF: true }
      ),
      getItem(t(`${tKey}sbom`), `/${DISCOVERY_ROOT}/${DISCOVERY.SBOM}`)
    ]),
    getItem(t(`${tKey}ai`), "ai", <AiDiscovery />, [
      getItem(t(`${tKey}agents`), "/ai/agents"),
      getItem(t(`${tKey}workflows`), "/ai/workflows", undefined, undefined, {
        aiDiscoveryLive: true
      }),
      getItem(t(`${tKey}mcpServers`), "/ai/mcp-servers", undefined, undefined, {
        aiDiscoveryLive: true
      }),
      getItem(t(`${tKey}tools`), "/ai/tools"),
      getItem(t(`${tKey}models`), "/ai/models"),
      getItem(
        t(`${tKey}knowledgeSources`),
        "/ai/knowledge-sources",
        undefined,
        undefined,
        { aiDiscoveryLive: true }
      )
    ]),
    getItem(t(`${tKey}triage`), "triage", <SortAscendingOutlined />, [
      getItem(t(`${tKey}issues`), issuesKey, undefined, undefined, {
        defaultQueryString: issueQueryString
      }),
      getItem(
        t(`${tKey}incidents`),
        `/${TRIAGE_ROOT}/${TRIAGE.INCIDENTS}`,
        undefined,
        undefined,
        {
          count: getIncidentsCount(pathname),
          soon: enableIncidentsExist && !attr.adrIncidentsEnabledFF,
          beta:
            enableIncidentsExist &&
            attr.adrIncidentsEnabledFF &&
            !attr.isInternalTenant,
          adrIncidentsEnabledFF: true, // Always show menu item (page shows Loader, Coming Soon or actual content)
          permissions: { resource: "incidents", action: "read" },
          removeOnSelfHosting: true
        }
      )
    ]),
    getItem(t(`${tKey}governance`), "governance", <BankOutlined />, [
      getItem("AI Policies", `/${GOVERNANCE_ROOT}/ai`, undefined, undefined, {
        demo: true,
        aiPolicies: true
      }),
      getItem(
        t(`${tKey}scmPolicies`),
        t(`${tKey}scmPolicies`),
        undefined,
        filterMenuItems(
          [
            getItem(
              t(`${tKey}openSourcePolicies`),
              `/${GOVERNANCE_ROOT}/${GOVERNANCE.SCM}`
            ),
            getItem(
              t(`${tKey}codePolicies`),
              `/${GOVERNANCE_ROOT}/${GOVERNANCE.CODE}`
            )
          ],
          attr
        )
      ),
      getItem(
        t(`${tKey}ciPolicies`),
        t(`${tKey}ciPolicies`),
        undefined,
        filterMenuItems(
          [
            getItem(
              t(`${tKey}openSourceCIPolicies`),
              `/${GOVERNANCE_ROOT}/${GOVERNANCE.CI_SCA}`
            ),
            getItem(
              t(`${tKey}codeCIPolicies`),
              `/${GOVERNANCE_ROOT}/${GOVERNANCE.CI_SAST}`
            )
          ],
          attr
        )
      ),
      getItem(
        t(`${tKey}scanHistory`),
        `/${GOVERNANCE_ROOT}/${SCAN_HISTORY}`,
        undefined,
        undefined,
        {
          permissions: {
            resource: "governance.scan-history",
            action: "read"
          }
        }
      ),
      getItem(
        t(`${tKey}automatedActions`),
        `/${GOVERNANCE_ROOT}/${WORKFLOWS}`,
        undefined,
        undefined,
        { removeOnSelfHosting: true }
      ),
      getItem(
        t(`${tKey}threatDetectionPolicies`),
        `/${GOVERNANCE_ROOT}/${GOVERNANCE.THREAT_DETECTION_POLICIES}`,
        undefined,
        undefined,
        { soon, tooltip, removeOnSelfHosting: true }
      )
    ]),
    getItem(t(`${tKey}reports`), `/${REPORTS_ROOT}`, <AuditOutlined />)
  ]
}

export const filterMenuItems = (
  items: ItemMenu[],
  {
    isDemo,
    isInternalTenant,
    vmInstancesEnabledFF,
    adrIncidentsEnabledFF,
    aiDiscovery,
    aiPoliciesFF,
    hasPermission
  }: {
    hasPermission: (permission: Permission) => boolean | undefined
    isDemo?: boolean
    isInternalTenant?: boolean
    vmInstancesEnabledFF?: boolean
    adrIncidentsEnabledFF?: boolean
    aiDiscovery?: Features.AiDiscoveryMode
    aiPoliciesFF?: boolean
  }
) => {
  const selfHosting = isSelfHosted()
  const { ROOT: REPORTS_ROOT } = NAMES_ROUTES.REPORTS

  return items
    .map((item: ItemMenu, i) => {
      const { children, customProps, ...rest } = item

      if (!children) return rest

      return {
        children: [
          { key: `${i}_title`, label: item.label, disabled: true } as ItemMenu,
          ...children
        ]
          .filter(({ customProps }) => {
            if (!customProps) return true

            if (customProps.removeOnSelfHosting && selfHosting) return false

            // Demo tenants, and those with live AI policies when it says so
            if (customProps.demo)
              return (
                !!isDemo ||
                (!!customProps.aiPolicies &&
                  isLiveAiPoliciesEnabled(aiDiscovery, aiPoliciesFF))
              )
            if (customProps.internalTenant) return !!isInternalTenant
            if (customProps.vmInstancesEnabledFF) return vmInstancesEnabledFF
            if (customProps.aiDiscoveryLive)
              return isAiDiscoveryLive(aiDiscovery)

            if (
              customProps.adrIncidentsEnabledFF &&
              adrIncidentsEnabledFF === false
            )
              return true

            if (customProps.permissions)
              return !!hasPermission(customProps.permissions)

            return true
          })
          .map(({ customProps, ...rest }) => rest),
        ...rest
      }
    })
    .filter((item) => {
      if (item.key === "ai") return isAiDiscoveryEnabled(aiDiscovery)

      if (!selfHosting) return true

      if (item.key === `/${REPORTS_ROOT}`) return false

      return true
    })
}

export const filterItemsByItemKey = (items: ItemType[], itemKey: string) =>
  items
    .filter((item: any) =>
      (item as { children?: { key: string }[] }).children?.some(
        ({ key }) => key === itemKey
      )
    )
    .map((item) => (item as { key: string }).key)
